import { useEffect, useMemo, useState } from 'react';
import { Loader2, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import espnowService, {
  relayDisplayName,
  type FieldCommand,
  type EspNowRouter,
} from '../../services/espnowIrrigation';
import type { SensorRule } from '../../services/sensorRules';
import { commandsForRelay } from '../../utils/commandActivity';
import { ruleRelayTargets } from '../../utils/sensorRuleRelays';
import RecentActivity from './RecentActivity';

/**
 * Did the valve this channel's rules drive actually do anything?
 *
 * The rule history beside this answers a different question, and the difference
 * matters. `actions_run: [{ type: 'relay_on', ok: true }]` means the server
 * queued a SET_RELAY — not that the site collected it, not that the node armed
 * it, and not that any water moved. A tank-fill rule firing faultlessly every
 * night against a bridge that stopped polling on Tuesday reads, on that page
 * alone, as a working automation.
 *
 * So this shows what `field_commands` recorded for exactly the valves those
 * rules address. Anything else the site was asked to do belongs on the
 * controller page; a sensor channel is only accountable for its own outputs.
 */

interface Props {
  /** The rules for the channel currently on screen. */
  rules: SensorRule[];
}

interface ControllerData {
  commands: FieldCommand[];
  routers: EspNowRouter[];
}

type State =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; byController: Record<string, ControllerData> }
  /**
   * Kept apart from an empty log. "Nothing has driven this valve" is a claim
   * about the hardware, and making it over a failed request states something
   * this page does not know.
   */
  | { status: 'failed' };

export default function SensorRuleRelayActivity({ rules }: Props) {
  const targets = useMemo(() => ruleRelayTargets(rules), [rules]);
  const [state, setState] = useState<State>({ status: 'idle' });

  // One pair of requests per controller, not per valve: a rule set almost
  // always sits on one site, and two valves on it share a command log.
  const controllerIds = useMemo(
    () => [...new Set(targets.map(t => t.controller_id))].sort(),
    [targets],
  );
  const controllerKey = controllerIds.join(',');

  useEffect(() => {
    if (controllerIds.length === 0) { setState({ status: 'idle' }); return; }
    let cancelled = false;
    setState({ status: 'loading' });

    Promise.all(controllerIds.map(async (cid) => {
      const [commands, routers] = await Promise.all([
        espnowService.getCommandLog(cid, 50),
        // Only used to name the valve. A site that will not list its nodes
        // still has a command log worth showing, so this failure is absorbed.
        espnowService.getRouters(cid).catch(() => [] as EspNowRouter[]),
      ]);
      if (!Array.isArray(commands)) throw new Error('command log unavailable');
      return [cid, { commands, routers }] as const;
    }))
      .then(entries => {
        if (!cancelled) setState({ status: 'ready', byController: Object.fromEntries(entries) });
      })
      .catch(() => { if (!cancelled) setState({ status: 'failed' }); });

    return () => { cancelled = true; };
    // controllerKey stands in for the array, which is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controllerKey]);

  // A channel whose rules only notify drives nothing, and a card saying so on
  // every reading-only sensor is a card nobody reads.
  if (targets.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Zap className="w-4 h-4" />
          What these rules actually did
        </CardTitle>
        <p className="text-xs text-gray-500">
          The rule history above says a rule fired and its action was accepted. This is
          what the site did with it.
        </p>
      </CardHeader>

      <CardContent className="pt-0 space-y-4">
        {state.status === 'loading' && (
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Reading the command log…
          </div>
        )}

        {state.status === 'failed' && (
          <div className="text-sm text-amber-700">
            Could not read the command log for this site, so what happened to these
            valves is unknown — not that nothing happened.
          </div>
        )}

        {state.status === 'ready' && targets.map(target => {
          const data = state.byController[target.controller_id];
          const router = data?.routers.find(
            r => r.router_mac.toUpperCase() === target.router_mac.toUpperCase(),
          );
          const valve = relayDisplayName(null, router?.relay_labels, target.relay_index);
          const node = router?.name || target.router_mac.slice(-6);
          const forValve = commandsForRelay(
            data?.commands ?? [], target.router_mac, target.relay_index,
          );

          return (
            <div key={`${target.controller_id}|${target.router_mac}|${target.relay_index}`}>
              <div className="text-sm font-medium text-gray-800 break-words">
                {node} · {valve}
              </div>
              <p className="text-xs text-gray-500 break-words">
                Driven by {target.rules.join(', ')}
                {/* A valve every rule on it is switched off still has a
                    history, and reading that history without knowing nothing
                    can add to it invites the wrong conclusion. */}
                {!target.enabled && ' — every rule on it is switched off'}
              </p>
              {forValve.length === 0 ? (
                <p className="mt-1 text-xs text-gray-500">
                  These rules have not driven this valve yet.
                </p>
              ) : (
                <RecentActivity
                  results={[]}
                  commands={forValve}
                  routers={data?.routers ?? []}
                  transport={null}
                  title={null}
                  limit={5}
                  compact
                />
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
